import { hasNativePortableText } from '../public-format/native-text-contract';
import { editorDocumentToPortableScene } from '../public-format/portable-scene-adapter';
import { portableSceneDiagnostics, serializePortableScene, verifyPortableSceneAssets } from '../public-format/portable-scene';
import { packPortableScene } from '../public-format/portable-scene-package';
import { packMaipoPublicScene, publicSceneDiagnostics, sceneSupportDiagnostics, serializeMaipoPublicScene, verifyPublicAssets } from '@maipo/format';
import type { EditorDocument } from '../core/model';
import { throwIfAborted } from '../codecs/types';
import { editorDocumentToPublicScene } from '../public-format/adapter';
import { downloadBlob } from './files';

export interface EncodedPublicScene { bytes: Uint8Array; mediaType: 'application/json' | 'application/zip'; fileName: string; diagnostics: string[] }

/**
 * Encode a detached public projection (JSON or package) without downloading
 * it. "Export public scene" and the agent bridge's `public-json` export both
 * call this, so they produce the same bytes for the same revision.
 */
export async function encodePublicScene(document: EditorDocument, format: 'json' | 'package', signal?: AbortSignal): Promise<EncodedPublicScene> {
  throwIfAborted(signal);
  if (hasNativePortableText(document.root)) {
    const scene = await editorDocumentToPortableScene(document, signal);
    const errors = (await verifyPortableSceneAssets(scene)).filter(item => item.severity === 'error');
    if (errors.length) throw new Error(errors.map(item => item.message).join(' '));
    throwIfAborted(signal);
    const bytes = format === 'json' ? new TextEncoder().encode(serializePortableScene(scene)) : await packPortableScene(scene, signal);
    if (format === 'json' && bytes.byteLength > 64 * 1024 * 1024) throw new Error('Portable scene JSON exceeds the 64 MiB limit. Use package export.');
    throwIfAborted(signal);
    const name = scene.name.replace(/[\\/:*?"<>|]+/g, '-').trim() || 'Untitled';
    return {
      bytes,
      mediaType: format === 'json' ? 'application/json' : 'application/zip',
      fileName: `${name}.draft.public.maipo${format === 'json' ? '.json' : ''}`,
      diagnostics: ['Unpublished scene 0.2-draft; published 0.1 readers will reject it.', ...portableSceneDiagnostics(scene).filter(item => item.severity !== 'error').map(item => item.message)],
    };
  }
  // The adapter clones resources and rejects unsupported native semantics before
  // any asynchronous work or download. Later edits cannot change this snapshot.
  const scene = editorDocumentToPublicScene(document);
  const diagnostics = [...sceneSupportDiagnostics(scene), ...publicSceneDiagnostics(scene), ...await verifyPublicAssets(scene)];
  const errors = diagnostics.filter(item => item.severity === 'error');
  if (errors.length) throw new Error(errors.map(item => item.message).join(' '));
  throwIfAborted(signal);
  let bytes: Uint8Array;
  if (format === 'json') {
    bytes = new TextEncoder().encode(serializeMaipoPublicScene(scene));
    if (bytes.byteLength > 64 * 1024 * 1024) throw new Error('Public scene JSON exceeds the 64 MiB limit. Use public package export.');
  } else {
    bytes = await packMaipoPublicScene(scene, signal);
  }
  throwIfAborted(signal);
  const name = scene.name.replace(/[\\/:*?"<>|]+/g, '-').trim() || 'Untitled';
  return {
    bytes,
    mediaType: format === 'json' ? 'application/json' : 'application/zip',
    fileName: `${name}.public.maipo${format === 'json' ? '.json' : ''}`,
    diagnostics: diagnostics.filter(item => item.severity !== 'error').map(item => item.message),
  };
}

/** Export a detached public projection; never change native save/dirty state. */
export async function exportPublicScene(document: EditorDocument, format: 'json' | 'package', signal?: AbortSignal): Promise<string[]> {
  const encoded = await encodePublicScene(document, format, signal);
  downloadBlob(new Blob([encoded.bytes], { type: encoded.mediaType }), encoded.fileName);
  return encoded.diagnostics;
}
